import { Platform } from 'react-native';
import NfcManager, { NfcTech, Ndef, NfcEvents, TagEvent } from 'react-native-nfc-manager';

// NFC tag payload identifiers used to distinguish clock-in vs clock-out tags.
// Tags should be programmed with NDEF Text records containing these values.
export const NFC_TAG_CLOCK_IN = 'SGA_CLOCK_IN';
export const NFC_TAG_CLOCK_OUT = 'SGA_CLOCK_OUT';
export const NFC_TAG_PREFIX = 'SGA_CLOCK_';

export type NfcClockAction = 'CLOCK_IN' | 'CLOCK_OUT';

export interface NfcTagResult {
  action: NfcClockAction;
  tagId: string;
  rawPayload: string;
}

class NfcService {
  private initialized = false;

  /**
   * Initialize NFC manager. Call once at app startup.
   */
  async init(): Promise<boolean> {
    try {
      const supported = await NfcManager.isSupported();
      if (!supported) {
        console.warn('NFC is not supported on this device');
        return false;
      }
      await NfcManager.start();
      this.initialized = true;
      return true;
    } catch (error) {
      console.error('Failed to initialize NFC:', error);
      return false;
    }
  }

  /**
   * Check if NFC is supported on this device.
   */
  async isSupported(): Promise<boolean> {
    try {
      return await NfcManager.isSupported();
    } catch {
      return false;
    }
  }

  /**
   * Check if NFC is enabled in device settings.
   * iOS has no user-facing NFC toggle — if the hardware supports it, it's available.
   */
  async isEnabled(): Promise<boolean> {
    try {
      if (Platform.OS === 'ios') {
        // iOS doesn't expose an NFC enabled/disabled setting.
        // If the device supports NFC, it's always available.
        return await NfcManager.isSupported();
      }
      return await NfcManager.isEnabled();
    } catch {
      return false;
    }
  }

  /**
   * Read an NFC tag and parse the SGA clock action from it.
   * Returns the clock action (CLOCK_IN or CLOCK_OUT) or null if invalid.
   */
  async readClockTag(): Promise<NfcTagResult | null> {
    if (!this.initialized) {
      const ok = await this.init();
      if (!ok) return null;
    }

    try {
      await NfcManager.requestTechnology(NfcTech.Ndef);
      const tag = await NfcManager.getTag();

      if (!tag) {
        return null;
      }

      const tagId = tag.id || 'unknown';
      const payload = this.parseNdefPayload(tag);

      if (!payload) {
        return null;
      }

      const action = this.parseClockAction(payload);
      if (!action) {
        return null;
      }

      return { action, tagId, rawPayload: payload };
    } catch (error) {
      console.error('NFC read error:', error);
      return null;
    } finally {
      this.cancelRead();
    }
  }

  /**
   * Cancel any pending NFC read operation.
   */
  cancelRead(): void {
    try {
      NfcManager.cancelTechnologyRequest();
    } catch {
      // Ignore cancellation errors
    }
  }

  /**
   * Write an SGA clock action to an NFC tag (for admin setup).
   */
  async writeClockTag(action: NfcClockAction): Promise<boolean> {
    if (!this.initialized) {
      const ok = await this.init();
      if (!ok) return false;
    }

    const payload = action === 'CLOCK_IN' ? NFC_TAG_CLOCK_IN : NFC_TAG_CLOCK_OUT;

    try {
      await NfcManager.requestTechnology(NfcTech.Ndef);

      const bytes = Ndef.encodeMessage([Ndef.textRecord(payload)]);
      if (bytes) {
        await NfcManager.ndefHandler.writeNdefMessage(bytes);
        return true;
      }
      return false;
    } catch (error) {
      console.error('NFC write error:', error);
      return false;
    } finally {
      this.cancelRead();
    }
  }

  /**
   * Register a background tag discovery listener.
   * Used so the app can respond to NFC taps even when not actively scanning.
   */
  registerTagListener(callback: (result: NfcTagResult | null) => void): void {
    NfcManager.setEventListener(NfcEvents.DiscoverTag, (tag: TagEvent) => {
      const payload = this.parseNdefPayload(tag);
      if (!payload) {
        callback(null);
        return;
      }
      const action = this.parseClockAction(payload);
      if (!action) {
        callback(null);
        return;
      }
      callback({
        action,
        tagId: tag.id || 'unknown',
        rawPayload: payload,
      });
    });
  }

  /**
   * Remove the background tag discovery listener.
   */
  removeTagListener(): void {
    NfcManager.setEventListener(NfcEvents.DiscoverTag, null);
  }

  /**
   * Extract text payload from an NDEF tag.
   */
  private parseNdefPayload(tag: any): string | null {
    try {
      if (!tag?.ndefMessage || tag.ndefMessage.length === 0) {
        return null;
      }

      const record = tag.ndefMessage[0];
      if (!record?.payload || record.payload.length === 0) {
        return null;
      }

      // NDEF Text record: first byte is status byte (language code length),
      // followed by language code, then the actual text.
      const payload = record.payload;
      const statusByte = payload[0];
      const languageCodeLength = statusByte & 0x3f;
      const textBytes = payload.slice(1 + languageCodeLength);

      return String.fromCharCode(...textBytes);
    } catch (error) {
      console.error('Error parsing NDEF payload:', error);
      return null;
    }
  }

  /**
   * Parse a text payload into a clock action.
   */
  private parseClockAction(payload: string): NfcClockAction | null {
    const trimmed = payload.trim().toUpperCase();

    if (trimmed === NFC_TAG_CLOCK_IN) {
      return 'CLOCK_IN';
    }
    if (trimmed === NFC_TAG_CLOCK_OUT) {
      return 'CLOCK_OUT';
    }

    return null;
  }

  /**
   * Open device NFC settings (useful when NFC is disabled).
   */
  async goToNfcSettings(): Promise<void> {
    try {
      await NfcManager.goToNfcSetting();
    } catch {
      // Not all platforms support this
    }
  }

  /**
   * Clean up NFC resources.
   */
  async cleanup(): Promise<void> {
    this.removeTagListener();
    this.cancelRead();
    this.initialized = false;
  }
}

// Singleton instance
const nfcService = new NfcService();
export default nfcService;
